import React, { useState, useMemo, useEffect, useCallback } from "react";
import {
    Card,
    Button,
    Form,
    Row,
    Col,
    Modal,
    Badge,
    Tab,
    Nav,
    Spinner,
    Alert
} from "react-bootstrap";

import {
    FaWhatsapp,
    FaInstagram,
    FaTelegram,
    FaPlus
} from "react-icons/fa";

import { providers } from "../../config/integrations/providers";
import connectionService from "../../services/connectionService";

import ProviderSelector from "./ConnectionWizard/ProviderSelector";
import ConfigTab from "./ConnectionWizard/ConfigTab";
import IntervalsTab from "./ConnectionWizard/IntervalsTab";
import WebhooksTab from "./ConnectionWizard/WebhooksTab";
import TestTab from "./ConnectionWizard/TestTab";

const FORM_INITIAL_STATE = {
    name: "",
    config: {},
    intervals: {
        automation: 5,
        attendant: 5,
        typing: 2
    },
    webhooks: {
        url: "",
        token: "",
        events: {},
        hmac: false,
        retry: false,
    },
};

const ConexoesTab = () => {

    const [showModal, setShowModal]     = useState(false);
    const [step, setStep]               = useState("select");
    const [search, setSearch]           = useState("");
    const [activeTab, setActiveTab]     = useState("config");
    const [channelKey, setChannelKey]   = useState("whatsapp");
    const [providerKey, setProviderKey] = useState(null);
    const [form, setForm]               = useState(FORM_INITIAL_STATE);

    // --- ESTADOS DA API ---
    const [connections, setConnections]   = useState([]);
    const [loading, setLoading]           = useState(false);
    const [saving, setSaving]             = useState(false);
    const [error, setError]               = useState(null);
    const [editingId, setEditingId]       = useState(null); // null = criando, number = editando

    // -------------------------------------------------------
    // CARREGAR CONEXÕES
    // -------------------------------------------------------
    const fetchConnections = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await connectionService.list();
            setConnections(data);
        } catch (err) {
            setError("Erro ao carregar conexões. Tente novamente.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchConnections();
    }, [fetchConnections]);

    // -------------------------------------------------------
    // SALVAR (CRIAR OU EDITAR)
    // -------------------------------------------------------
    const handleSave = async () => {
        setSaving(true);
        setError(null);
        try {
            const payload = {
                name:     form.name,
                channel:  channelKey,
                provider: providerKey,
                config:   form.config,
                intervals: form.intervals,
                webhooks: form.webhooks,
            };

            if (editingId) {
                // Atualizar conexão existente
                const updated = await connectionService.update(editingId, payload);
                setConnections(prev =>
                    prev.map(c => c.id === editingId ? updated : c)
                );
            } else {
                // Criar nova conexão
                const created = await connectionService.create(payload);
                setConnections(prev => [...prev, created]);
            }

            setShowModal(false);
        } catch (err) {
            const message = err.response?.data?.message || "Erro ao salvar conexão.";
            setError(message);
        } finally {
            setSaving(false);
        }
    };

    // -------------------------------------------------------
    // TOGGLE (ATIVAR / DESATIVAR)
    // -------------------------------------------------------
    const toggleConnection = async (id) => {
        try {
            const updated = await connectionService.toggle(id);
            setConnections(prev =>
                prev.map(c => c.id === id ? updated : c)
            );
        } catch (err) {
            setError("Erro ao alterar status da conexão.");
        }
    };

    // -------------------------------------------------------
    // ABRIR MODAL
    // -------------------------------------------------------
    const openNewConnection = () => {
        setEditingId(null);
        setForm(FORM_INITIAL_STATE);
        setStep("select");
        setActiveTab("config");
        setProviderKey(null);
        setChannelKey("whatsapp");
        setError(null);
        setShowModal(true);
    };

    const openEditConnection = (connection) => {
        setEditingId(connection.id);
        setChannelKey(connection.channel);
        setProviderKey(connection.provider);
        setForm({
            name:      connection.name,
            config:    connection.config    || {},
            intervals: connection.intervals || FORM_INITIAL_STATE.intervals,
            webhooks:  connection.webhooks  || FORM_INITIAL_STATE.webhooks,
        });
        setStep("config"); // Pula a seleção de provider ao editar
        setActiveTab("config");
        setError(null);
        setShowModal(true);
    };

    // -------------------------------------------------------
    // HELPERS
    // -------------------------------------------------------
    const currentProvider = providers?.[channelKey]?.providers?.[providerKey];

    const filteredConnections = useMemo(() => {
        if (search.length < 2) return connections;
        return connections.filter(c =>
            c.name.toLowerCase().includes(search.toLowerCase())
        );
    }, [connections, search]);

    const getChannelIcon = (channel) => {
        switch (channel) {
            case "whatsapp":  return <FaWhatsapp color="#25D366" />;
            case "instagram": return <FaInstagram color="#E1306C" />;
            case "telegram":  return <FaTelegram color="#229ED9" />;
            default:          return null;
        }
    };

    // -------------------------------------------------------
    // RENDER
    // -------------------------------------------------------
    return (
        <>
            {/* ERRO GLOBAL */}
            {error && (
                <Alert
                    variant="danger"
                    dismissible
                    onClose={() => setError(null)}
                    className="mb-3"
                >
                    {error}
                </Alert>
            )}

            {/* PESQUISA */}
            <Card className="mb-3">
                <Card.Body>
                    <Row>
                        <Col md={9}>
                            <Form.Control
                                placeholder="Pesquisar conexões..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </Col>
                        <Col md={3} className="text-end">
                            <Button onClick={openNewConnection}>
                                <FaPlus className="me-2" />
                                Nova conexão
                            </Button>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* LOADING */}
            {loading && (
                <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                    <div className="mt-2 text-muted">Carregando conexões...</div>
                </div>
            )}

            {/* CARDS */}
            {!loading && (
                <Row>
                    {filteredConnections.map(connection => (
                        <Col md={4} key={connection.id} className="mb-3">
                            <Card
                                className="shadow-sm h-100"
                                style={{ cursor: "pointer" }}
                                onClick={() => openEditConnection(connection)}
                            >
                                <Card.Body>
                                    <div className="d-flex justify-content-between">
                                        <div>
                                            <div className="d-flex align-items-center">
                                                {getChannelIcon(connection.channel)}
                                                <strong className="ms-2">
                                                    {connection.name}
                                                </strong>
                                            </div>
                                            <small className="text-muted">
                                                {connection.provider}
                                            </small>
                                        </div>
                                        {/* Impede que o toggle abra o modal de edição */}
                                        <div onClick={(e) => e.stopPropagation()}>
                                            <Form.Check
                                                type="switch"
                                                checked={!!connection.is_active}
                                                onChange={() => toggleConnection(connection.id)}
                                            />
                                        </div>
                                    </div>
                                    <Badge
                                        bg={connection.is_active ? "success" : "secondary"}
                                        className="mt-3"
                                    >
                                        {connection.is_active ? "Ativo" : "Inativo"}
                                    </Badge>
                                </Card.Body>
                            </Card>
                        </Col>
                    ))}

                    {/* CARD NOVA CONEXÃO */}
                    <Col md={4}>
                        <Card
                            className="h-100 text-center d-flex justify-content-center"
                            style={{ cursor: "pointer", minHeight: 160 }}
                            onClick={openNewConnection}
                        >
                            <Card.Body className="d-flex flex-column justify-content-center">
                                <FaPlus size={34} className="text-primary mb-2" />
                                Nova conexão
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            )}

            {/* MODAL */}
            <Modal
                show={showModal}
                onHide={() => setShowModal(false)}
                size="xl"
                backdrop="static"
                centered
                dialogClassName="modal-conexao"
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        {editingId ? "Editar conexão" : "Nova conexão"}
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    {/* ERRO NO MODAL */}
                    {error && (
                        <Alert
                            variant="danger"
                            dismissible
                            onClose={() => setError(null)}
                        >
                            {error}
                        </Alert>
                    )}

                    {step === "select" ? (
                        <ProviderSelector
                            providers={providers}
                            channelKey={channelKey}
                            setChannelKey={setChannelKey}
                            providerKey={providerKey}
                            setProviderKey={setProviderKey}
                            onNext={() => setStep("config")}
                        />
                    ) : (
                        <>
                            <div className="mb-3 border rounded p-2 bg-light">
                                <strong>{providers[channelKey]?.label}</strong>
                                {" → "}
                                {currentProvider?.label}
                            </div>

                            <Tab.Container
                                activeKey={activeTab}
                                onSelect={setActiveTab}
                            >
                                <Nav variant="tabs">
                                    <Nav.Item>
                                        <Nav.Link eventKey="config">Configuração</Nav.Link>
                                    </Nav.Item>
                                    <Nav.Item>
                                        <Nav.Link eventKey="intervals">Intervalos</Nav.Link>
                                    </Nav.Item>
                                    <Nav.Item>
                                        <Nav.Link eventKey="webhooks">Webhooks</Nav.Link>
                                    </Nav.Item>
                                    {/* Aba de teste só disponível ao editar */}
                                    {editingId && (
                                        <Nav.Item>
                                            <Nav.Link eventKey="test">Teste</Nav.Link>
                                        </Nav.Item>
                                    )}
                                </Nav>

                                <Tab.Content className="pt-4">
                                    <Tab.Pane eventKey="config">
                                        <ConfigTab
                                            form={form}
                                            setForm={setForm}
                                            provider={currentProvider}
                                        />
                                    </Tab.Pane>
                                    <Tab.Pane eventKey="intervals">
                                        <IntervalsTab
                                            form={form}
                                            setForm={setForm}
                                        />
                                    </Tab.Pane>
                                    <Tab.Pane eventKey="webhooks">
                                        <WebhooksTab
                                            form={form}
                                            setForm={setForm}
                                        />
                                    </Tab.Pane>
                                    {editingId && (
                                        <Tab.Pane eventKey="test">
                                            <TestTab
                                                connectionId={editingId}
                                                form={form}
                                            />
                                        </Tab.Pane>
                                    )}
                                </Tab.Content>
                            </Tab.Container>
                        </>
                    )}
                </Modal.Body>

                <Modal.Footer>
                    {step === "config" && !editingId && (
                        <Button
                            variant="outline-secondary"
                            onClick={() => setStep("select")}
                        >
                            Voltar
                        </Button>
                    )}
                    <Button
                        variant="secondary"
                        onClick={() => setShowModal(false)}
                        disabled={saving}
                    >
                        Cancelar
                    </Button>
                    {step === "config" && (
                        <Button
                            variant="success"
                            onClick={handleSave}
                            disabled={saving}
                        >
                            {saving ? (
                                <>
                                    <Spinner size="sm" className="me-2" />
                                    Salvando...
                                </>
                            ) : (
                                editingId ? "Atualizar conexão" : "Salvar conexão"
                            )}
                        </Button>
                    )}
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default ConexoesTab;
